<template>
  <section class="search-suggestions">
    <ul ref="listEl" class="grid-y">
      <li v-for="(item, index) in suggestions" :key="item" @click="emits('select', { item: item.sourceLabel, index })">
        <!-- eslint-disable vue/no-v-html -->
        <Link
          :href="buildUrl(item.sourceLabel, item.department)"
          class="suggestion undecorated-link text-left"
          :data-label="item.sourceLabel"
          :aria-role="'option'"
          @focus="selectTopSuggestion(item)"
          @mouseover="debounceSelectTopSuggestion(item)"
          @keydown.arrow-up.prevent="onKeydownArrowUp"
          @keydown.arrow-down.prevent="onKeydownArrowDown"
        >
          <span v-html="item.label"></span>
        </Link>
        <!-- eslint-enable -->
      </li>
    </ul>
  </section>
</template>

<script setup>
/* v8 ignore file -- @preserve */
import { computed, ref } from 'vue';
import { useDebounceFn } from '@vueuse/core';

import Link from '../common/Link';
import { SET_TOP_SUGGESTION } from '#shared/types/actions';
import getSearchPrefix from '~/utils/getSearchPrefix';
import { useSearchResultList } from '~/composables/accessibility/useSearchResultList';
import { normalizeSearchString } from '~/utils/normalizeSearchString';
import { SEARCH_PAGE_PREFIXES } from '#shared/constants';
import addQueryParam from '~/utils/addQueryParam';
import { buildTopSuggestionLabel } from '~/composables/dumb/useSearchSuggestions';

const emits = defineEmits(['select']);

const { isMcom } = useEnvProps();
const navHeader = useNavHeader();
const navSearchSuggestions = useNavSearchSuggestions();
const listEl = ref(null);
const { onKeydownArrowUp, onKeydownArrowDown } = useSearchResultList({ listEl });
const suggestions = computed(() => navSearchSuggestions.data);

const selectTopSuggestion = (item) => {
  const label = buildTopSuggestionLabel(item);

  navSearchSuggestions[SET_TOP_SUGGESTION](label);
};

const debounceSelectTopSuggestion = useDebounceFn((item) => {
  if (!document.querySelector(`[data-label="${item.sourceLabel}"]:hover`)) return;
  selectTopSuggestion(item);
}, 150);

const searchValue = computed(() => navHeader.searchValue);
const buildUrl = (label, department) => {
  const urlPrefix = getSearchPrefix();
  const labelWithDepartment = buildTopSuggestionLabel({ department, sourceLabel: label });
  const normalizedLabel = normalizeSearchString(labelWithDepartment, urlPrefix === SEARCH_PAGE_PREFIXES.featured);
  const normalizedSearchValue = normalizeSearchString(searchValue.value, false);

  return addQueryParam(`${urlPrefix}${normalizedLabel}`, {
    cm_kws_ac: isMcom ? normalizedSearchValue : '',
    link_type: department ? 'department' : '',
    ss: true,
  });
};

defineExpose({ listEl });
</script>

<style lang="scss">
.ui-keyword-highlight {
  font-weight: bold;
}

.ui-keyword-department {
  display: inline-block;
  margin-left: 4px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  letter-spacing: 0.01em;
  color: $gray-1-color;
  margin-top: 4px;
}
</style>

<style scoped lang="scss">
@import '~/assets/scss/mixins/index.scss';

a {
  color: $gray-1-color;
  display: block;
  &:hover,
  &:focus {
    color: $highlight-color;
  }
}

.suggestion {
  color: $black;
}

ul {
  gap: map-get($grid-margin-gutters, 'large');
}

@include breakpoint(large up) {
  ul {
    gap: map-get($grid-margin-gutters, 'medium');
  }
}
</style>

<style scoped lang="scss" brand="mcom">
@import '~/assets/scss/mixins.mcom.scss';

ul {
  gap: 16px;
  @include breakpoint(medium down) {
    gap: 20px;
  }
}
</style>

<style scoped lang="scss" brand="bcom">
@import '~/assets/scss/mixins.bcom.scss';

ul {
  gap: 16px;
  @include breakpoint(medium down) {
    gap: 22px;
  }
  a.suggestion {
    line-height: 18px;
  }
}
</style>
